import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Loader2, Timer } from "lucide-react";
import { navigate, useQueryParams } from "raviger";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getCurrencySymbol } from "@/components/ui/monetary-display";

import ConfirmActionDialog from "@/components/Common/ConfirmActionDialog";
import Loading from "@/components/Common/Loading";

import { usePatientContext } from "@/hooks/usePatientUser";

import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import {
  clearPublicPendingBooking,
  getPublicPendingBookingStorageKey,
  loadPublicPendingBooking,
  savePublicPendingBooking,
} from "@/pages/PublicAppointments/pendingBookingStorage";
import { PublicPatientRead } from "@/types/emr/patient/patient";
import publicPatientApi from "@/types/emr/patient/publicPatientApi";
import PublicAppointmentApi from "@/types/scheduling/PublicAppointmentApi";
import {
  AppointmentStatus,
  PublicAppointment,
} from "@/types/scheduling/schedule";

interface PatientCardProps {
  patient: PublicPatientRead;
  selectedPatient: string | null;
  onSelectPatient: (patientId: string) => void;
  getPatienDobOrAge: (patient: PublicPatientRead) => string;
  disabled?: boolean;
}

function PatientCard({
  patient,
  selectedPatient,
  onSelectPatient,
  getPatienDobOrAge,
  disabled,
}: PatientCardProps) {
  const { t } = useTranslation();

  return (
    <Card
      key={patient.id}
      onClick={() => {
        if (!disabled) {
          onSelectPatient(patient.id);
        }
      }}
      className={cn(
        "cursor-pointer transition-all duration-200 rounded-xl shadow-md border",
        selectedPatient === patient.id
          ? "border-primary shadow-lg"
          : "hover:border-gray-300",
        disabled && "pointer-events-none opacity-70",
      )}
    >
      <CardHeader>
        <CardTitle className="capitalize text-lg font-semibold">
          {patient.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-gray-700 font-medium">
            {t("date_of_birth_age")}:
          </span>
          <span className="text-sm font-semibold">
            {getPatienDobOrAge(patient)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-700 font-medium">{t("sex")}:</span>
          <span className="text-sm font-semibold">
            {t(`GENDER__${patient.gender}`)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function PatientList({
  patients,
  selectedPatient,
  onSelectPatient,
  getPatienDobOrAge,
  disabled,
}: {
  patients: PublicPatientRead[];
  selectedPatient: string | null;
  onSelectPatient: (patientId: string) => void;
  getPatienDobOrAge: (patient: PublicPatientRead) => string;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-0 sm:p-4">
      {patients?.map((patient) => (
        <PatientCard
          key={patient.id}
          patient={patient}
          selectedPatient={selectedPatient}
          onSelectPatient={onSelectPatient}
          getPatienDobOrAge={getPatienDobOrAge}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function getSecondsLeft(expiry: string | null) {
  if (!expiry) return null;

  return Math.max(
    0,
    Math.floor((new Date(expiry).getTime() - Date.now()) / 1000),
  );
}

function formatTimer(secondsLeft: number | null) {
  if (secondsLeft === null) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function PatientSelect({
  facilityId,
  staffId,
}: {
  facilityId: string;
  staffId: string;
}) {
  const { t } = useTranslation();
  const [
    {
      slotId,
      date,
      reason,
      consultationTitle,
      consultationAmount,
      selectedPatient: selectedPatientQuery,
    },
  ] = useQueryParams();

  const patientUserContext = usePatientContext();
  const tokenData = patientUserContext?.tokenData;

  const queryClient = useQueryClient();
  const hasConsultationCharge =
    typeof consultationAmount === "string" ||
    typeof consultationTitle === "string";
  const schedulePath = `/facility/${facilityId}/appointments/${staffId}/book-appointment`;
  const storageKey = getPublicPendingBookingStorageKey(
    facilityId,
    staffId,
    slotId ?? "",
    tokenData?.phoneNumber ?? "",
  );
  const patientQueryKey = ["otp-patient", tokenData?.phoneNumber];
  const persistedPendingBooking = useMemo(
    () => loadPublicPendingBooking(storageKey),
    [storageKey],
  );
  const [selectedPatient, setSelectedPatient] = useState<string | null>(
    typeof selectedPatientQuery === "string" ? selectedPatientQuery : null,
  );
  const [pendingAppointmentId, setPendingAppointmentId] = useState<
    string | null
  >(persistedPendingBooking?.appointmentId ?? null);
  const [pendingExpiry, setPendingExpiry] = useState<string | null>(
    persistedPendingBooking?.pendingExpiry ?? null,
  );
  const [secondsLeft, setSecondsLeft] = useState<number | null>(() =>
    getSecondsLeft(persistedPendingBooking?.pendingExpiry ?? null),
  );
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [footerActionReady, setFooterActionReady] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const expiryHandledRef = useRef(false);

  useEffect(() => {
    if (typeof selectedPatientQuery === "string") {
      setSelectedPatient(selectedPatientQuery);
    }
  }, [selectedPatientQuery]);

  if (!staffId) {
    toast.error(t("staff_not_found"));
    navigate(`/facility/${facilityId}`);
  } else if (!tokenData) {
    toast.error(t("phone_number_not_found"));
    navigate(`/facility/${facilityId}/appointments/${staffId}/otp/send`);
  } else if (!slotId) {
    toast.error(t("selected_slot_not_found"));
    navigate(schedulePath);
  }

  const { data: patientData, isLoading } = useQuery({
    queryKey: patientQueryKey,
    queryFn: query.paginated(publicPatientApi.list, {
      headers: {
        Authorization: `Bearer ${tokenData.token}`,
        "Content-Type": "application/json",
      },
      pageSize: 100,
    }),
    enabled: !!tokenData.token,
    refetchOnMount: "always",
  });

  const appointmentsQuery = useQuery({
    queryKey: ["appointment", tokenData?.phoneNumber],
    queryFn: query(PublicAppointmentApi.getAppointments, {
      headers: {
        Authorization: `Bearer ${tokenData?.token}`,
      },
    }),
    enabled: !!tokenData?.token,
    refetchInterval: pendingAppointmentId ? 5000 : false,
  });

  const pendingAppointment = appointmentsQuery.data?.results.find(
    (appointment) => appointment.id === pendingAppointmentId,
  );

  const { mutateAsync: createAppointment, isPending: isCreatingAppointment } =
    useMutation({
      mutationFn: mutate(PublicAppointmentApi.createAppointment, {
        pathParams: { id: slotId ?? "" },
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
      }),
    });
  const { mutateAsync: confirmAppointment, isPending: isConfirmingPayment } =
    useMutation({
      mutationFn: mutate(PublicAppointmentApi.confirmAppointment, {
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
      }),
    });
  const { mutateAsync: cancelAppointment, isPending: isCancellingAppointment } =
    useMutation({
      mutationFn: mutate(PublicAppointmentApi.cancelAppointment, {
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
      }),
    });

  const patients = patientData?.results;
  const timerLabel = formatTimer(secondsLeft);

  const persistPendingReservation = useCallback(
    (appointment: PublicAppointment) => {
      savePublicPendingBooking(storageKey, {
        appointmentId: appointment.id,
        patientId: appointment.patient?.id ?? null,
        pendingExpiry: appointment.pending_expiry,
      });
      setPendingAppointmentId(appointment.id);
      setPendingExpiry(appointment.pending_expiry);
    },
    [storageKey],
  );

  const clearPendingReservation = useCallback(() => {
    clearPublicPendingBooking(storageKey);
    setPendingAppointmentId(null);
    setPendingExpiry(null);
    expiryHandledRef.current = false;
  }, [storageKey]);

  const refreshPublicBookingState = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["appointment", tokenData.phoneNumber],
    });
    await queryClient.invalidateQueries({
      queryKey: ["public-slots", facilityId, staffId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["public-availability-heatmap", facilityId, staffId],
    });
  }, [facilityId, queryClient, staffId, tokenData.phoneNumber]);

  const cancelPendingReservation = useCallback(async () => {
    if (!pendingAppointmentId) {
      clearPendingReservation();
      return;
    }

    await cancelAppointment({
      appointment: pendingAppointmentId,
    });
    clearPendingReservation();
    await refreshPublicBookingState();
  }, [
    cancelAppointment,
    clearPendingReservation,
    pendingAppointmentId,
    refreshPublicBookingState,
  ]);

  const handleSelectPatient = useCallback((patientId: string) => {
    setSelectedPatient(patientId);
  }, []);

  const handleBackToSchedule = async () => {
    if (isNavigatingBack) {
      return;
    }

    setIsNavigatingBack(true);

    try {
      if (pendingAppointmentId) {
        await cancelPendingReservation();
      }

      navigate(schedulePath, {
        query: {
          slotId,
          date,
          reason,
        },
      });
    } catch {
      setIsNavigatingBack(false);
    }
  };

  const handleAddNewPatient = async () => {
    navigate(
      `/facility/${facilityId}/appointments/${staffId}/patient-registration`,
      {
        query: {
          slotId,
          date,
          reason,
          consultationTitle,
          consultationAmount,
        },
      },
    );
  };

  const finalizeSuccessfulAppointment = useCallback(
    async (appointment: PublicAppointment) => {
      clearPendingReservation();

      await refreshPublicBookingState();
      toast.success(t("appointment_created_success"));
      navigate(
        `/facility/${facilityId}/appointments/${appointment.id}/success`,
        {
          query: appointment.charge_item ? { payment: "success" } : undefined,
          replace: true,
        },
      );
    },
    [clearPendingReservation, facilityId, refreshPublicBookingState, t],
  );

  const renderNoPatientFound = () => {
    return (
      <div>
        <span className="text-base font-medium">
          {t("no_patients_found_phone_number")}
        </span>
      </div>
    );
  };

  const getPatienDobOrAge = (patient: PublicPatientRead) => {
    if (patient.date_of_birth) {
      return dayjs(patient.date_of_birth).format("DD MMM YYYY");
    }
    const yearOfBirth = patient.year_of_birth;
    const age = dayjs().year() - yearOfBirth!;
    return `${age} years`;
  };

  const handleConfirmPayment = useCallback(() => {
    if (!selectedPatient || !pendingAppointmentId) {
      setShowPaymentConfirmation(false);
      return;
    }

    setShowPaymentConfirmation(false);

    void (async () => {
      const appointment = await confirmAppointment({
        appointment: pendingAppointmentId,
        patient: selectedPatient,
      });
      await finalizeSuccessfulAppointment(appointment);
    })();
  }, [
    confirmAppointment,
    finalizeSuccessfulAppointment,
    pendingAppointmentId,
    selectedPatient,
  ]);

  const handleConfirm = () => {
    if (!selectedPatient) return;

    if (hasConsultationCharge) {
      if (!pendingAppointmentId) return;

      setShowPaymentConfirmation(true);
      return;
    }

    void (async () => {
      const appointment = await createAppointment({
        patient: selectedPatient,
        note: reason,
      });

      await refreshPublicBookingState();

      if (appointment.status === AppointmentStatus.PENDING) {
        persistPendingReservation(appointment);
        return;
      }

      await finalizeSuccessfulAppointment(appointment);
    })();
  };

  const renderConsultationSummary = (className?: string) => {
    if (!hasConsultationCharge) return null;

    return (
      <Card
        className={cn(
          "border-primary-200 bg-white/95 shadow-lg backdrop-blur-sm",
          className,
        )}
      >
        <CardContent className="flex flex-col gap-4 p-4">
          <div>
            <div className="text-sm font-semibold text-gray-900">
              {t("consultation_fee_required")}
            </div>
            {consultationTitle && (
              <div className="text-sm text-gray-600">{consultationTitle}</div>
            )}
            <div className="mt-1 text-sm text-gray-600">
              {pendingAppointmentId
                ? t("slot_reserved_until_payment", {
                    defaultValue:
                      "This slot is reserved while you complete payment.",
                  })
                : t("slot_reservation_missing", {
                    defaultValue:
                      "This reservation was not found. Please return and choose the slot again.",
                  })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {pendingAppointmentId && timerLabel && (
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700">
                <Timer className="size-4" />
                {timerLabel}
              </div>
            )}
            {consultationAmount && (
              <div className="text-sm font-semibold text-gray-900">
                {getCurrencySymbol()}
                {consultationAmount}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  useEffect(() => {
    if (!pendingAppointmentId) return;
    if (!appointmentsQuery.data) return;
    // Only act when the appointment is actually found in the fetched results.
    if (!pendingAppointment) return;

    if (pendingAppointment.status === AppointmentStatus.BOOKED) {
      clearPendingReservation();
      return;
    }

    if (pendingAppointment.status === AppointmentStatus.PENDING) {
      // Refresh the stored expiry from the latest server value without
      // triggering unnecessary timer restarts (only update if changed).
      if (pendingAppointment.pending_expiry !== pendingExpiry) {
        persistPendingReservation(pendingAppointment);
      }
      return;
    }
  }, [
    appointmentsQuery.data,
    clearPendingReservation,
    pendingAppointment,
    pendingAppointmentId,
    pendingExpiry,
    persistPendingReservation,
  ]);

  useEffect(() => {
    if (!patients) return;

    if (patients.length === 0) {
      setSelectedPatient(null);
      return;
    }

    const patientIds = new Set(patients.map((patient) => patient.id));

    if (
      typeof selectedPatientQuery === "string" &&
      patientIds.has(selectedPatientQuery)
    ) {
      if (selectedPatient !== selectedPatientQuery) {
        setSelectedPatient(selectedPatientQuery);
      }
      return;
    }

    if (selectedPatient && patientIds.has(selectedPatient)) {
      return;
    }

    setSelectedPatient(patients[0].id);
  }, [patients, selectedPatient, selectedPatientQuery]);

  useEffect(() => {
    setSecondsLeft(getSecondsLeft(pendingExpiry));

    if (!pendingExpiry) return;

    const intervalId = window.setInterval(() => {
      setSecondsLeft(getSecondsLeft(pendingExpiry));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [pendingExpiry]);

  useEffect(() => {
    expiryHandledRef.current = false;
  }, [pendingAppointmentId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setFooterActionReady(true);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!hasConsultationCharge || !selectedPatient || !pendingAppointmentId) {
      setShowPaymentConfirmation(false);
    }
  }, [hasConsultationCharge, pendingAppointmentId, selectedPatient]);

  useEffect(() => {
    if (
      !pendingAppointmentId ||
      secondsLeft !== 0 ||
      expiryHandledRef.current
    ) {
      return;
    }

    expiryHandledRef.current = true;
    void (async () => {
      await cancelPendingReservation();
      toast.error(
        t("slot_reservation_expired", {
          defaultValue:
            "Slot reservation expired. Please choose the slot again.",
        }),
      );
      navigate(schedulePath, {
        query: {
          slotId,
          date,
          reason,
        },
        replace: true,
      });
    })();
  }, [
    cancelPendingReservation,
    date,
    facilityId,
    pendingAppointmentId,
    reason,
    secondsLeft,
    slotId,
    schedulePath,
    staffId,
    t,
  ]);

  return (
    <div className="container mx-auto max-w-4xl p-4 pb-32 lg:max-w-6xl">
      <ConfirmActionDialog
        open={showPaymentConfirmation}
        onOpenChange={setShowPaymentConfirmation}
        title={t("confirm_payment_booking_title", {
          defaultValue: "Confirm Payment",
        })}
        description={t("confirm_payment_booking_description", {
          defaultValue:
            "We'll finalize this reserved appointment and record the consultation payment.",
        })}
        onConfirm={handleConfirmPayment}
        confirmText={t("make_payment", {
          defaultValue: "Make Payment",
        })}
        disabled={
          isCreatingAppointment ||
          isConfirmingPayment ||
          isCancellingAppointment ||
          !pendingAppointmentId
        }
      />
      <div className="flex pb-4 justify-start">
        <Button
          variant="outline"
          className="border border-secondary-400"
          disabled={isNavigatingBack || isCancellingAppointment}
          onClick={() => void handleBackToSchedule()}
        >
          {isNavigatingBack && <Loader2 className="mr-2 size-4 animate-spin" />}
          <span className="text-sm">{t("back")}</span>
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 justify-between items-center my-6">
        <h3>{t("select_register_patient")}</h3>
        <Button
          variant="primary_gradient"
          className="w-full sm:w-auto"
          onClick={() => void handleAddNewPatient()}
        >
          <span className="bg-linear-to-b from-white/15 to-transparent"></span>
          {t("add_new_patient")}
        </Button>
      </div>
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-8">
        <div className="min-w-0">
          {renderConsultationSummary("mb-6 lg:hidden")}

          <div className="flex flex-col justify-center space-y-4">
            {isLoading ? (
              <div className="flex justify-center items-center">
                <Loading />
              </div>
            ) : (patients?.length ?? 0) > 0 ? (
              <PatientList
                patients={patients ?? []}
                selectedPatient={selectedPatient}
                onSelectPatient={(patientId) => {
                  void handleSelectPatient(patientId);
                }}
                getPatienDobOrAge={getPatienDobOrAge}
                disabled={isCreatingAppointment || isCancellingAppointment}
              />
            ) : (
              renderNoPatientFound()
            )}
          </div>
        </div>

        <div className="hidden lg:block">
          {renderConsultationSummary("sticky top-24")}
        </div>
      </div>

      {selectedPatient && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg">
          <div className="container mx-auto max-w-4xl flex justify-end gap-3 lg:max-w-6xl">
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={
                !footerActionReady ||
                isCreatingAppointment ||
                isConfirmingPayment ||
                isCancellingAppointment ||
                (hasConsultationCharge && !pendingAppointmentId)
              }
            >
              {(isCreatingAppointment ||
                isConfirmingPayment ||
                isCancellingAppointment) && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              {hasConsultationCharge
                ? t("make_payment", { defaultValue: "Make Payment" })
                : t("confirm")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
